import type { ReactElement } from 'react'
import { useEffect } from 'react'
import {
  Ban,
  CircleCheck,
  CircleSlash,
  CloudUpload,
  Download,
  Hourglass,
  Sparkles,
  TriangleAlert
} from 'lucide-react'
import type { JobDTO } from '@shared/types'
import { JobStatus, JobType, isJobActive, jobProgressPct } from '@shared/types'
import type { ProgressTone } from '@/components/ui'
import { Button, ProgressBar, Spinner } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatNumber, pluralize } from '@/lib/format'
import { useSettingsStore } from '@/stores/useSettingsStore'

export interface JobProgressBarProps {
  job: JobDTO
  onCancel: () => void
  cancelling?: boolean
}

const RUNNING_COPY: Record<JobType, string> = {
  FETCH: 'Fetching media library',
  GENERATE: 'Generating alt text',
  SAVE: 'Saving to WordPress'
}

const FINISHED_COPY: Record<JobType, string> = {
  FETCH: 'Media library up to date',
  GENERATE: 'Alt text generated',
  SAVE: 'Saved to WordPress'
}

const QUEUED_ICONS: Record<JobType, ReactElement> = {
  FETCH: <Download aria-hidden="true" />,
  GENERATE: <Sparkles aria-hidden="true" />,
  SAVE: <CloudUpload aria-hidden="true" />
}

const shells: Record<ProgressTone, string> = {
  accent: 'border-brand-500/30',
  success: 'border-ok-500/35',
  warning: 'border-warn-500/35',
  danger: 'border-bad-500/40'
}

const glyphs: Record<ProgressTone, string> = {
  accent: 'bg-accent-soft text-accent',
  success: 'bg-ok-100 text-ok-700 dark:bg-ok-950 dark:text-ok-300',
  warning: 'bg-warn-100 text-warn-700 dark:bg-warn-950 dark:text-warn-300',
  danger: 'bg-bad-100 text-bad-700 dark:bg-bad-950 dark:text-bad-300'
}

/** "1st in line" reads as a place in a queue; "position 1" reads as a database row. */
function ordinal(n: number): string {
  const teens = n % 100
  if (teens >= 11 && teens <= 13) return `${formatNumber(n)}th`
  const last = n % 10
  const suffix = last === 1 ? 'st' : last === 2 ? 'nd' : last === 3 ? 'rd' : 'th'
  return `${formatNumber(n)}${suffix}`
}

/**
 * The live view of one background job. A finished job keeps rendering for a
 * moment (the page owns that timer) so the outcome is readable — a bar that
 * vanishes the instant the last item lands reads as if nothing happened.
 *
 * QUEUED is a state in its own right rather than a progress bar at 0%: a batch
 * that has not started yet must never look like one that has, because the user's
 * next decision (wait, or cancel and come back) depends on the difference.
 */
export function JobProgressBar({ job, onCancel, cancelling = false }: JobProgressBarProps): ReactElement {
  const active = isJobActive(job)
  const queued = job.status === JobStatus.Queued
  // Only GENERATE competes for a generation slot. A queued FETCH or SAVE is simply
  // waiting for this site's current job to finish, which is a much shorter wait.
  const waitingForSlot = queued && job.type === JobType.Generate
  const position = job.queuePosition ?? 0

  const maxUsers = useSettingsStore((state) => state.status?.queue.maxConcurrentUsers ?? null)
  const loadStatus = useSettingsStore((state) => state.loadStatus)

  // The instance's slot count is only worth a request while somebody is waiting on
  // it, so it is fetched here rather than on every media page load.
  useEffect(() => {
    if (waitingForSlot && maxUsers === null) void loadStatus()
  }, [waitingForSlot, maxUsers, loadStatus])

  const processed = job.completed + job.failed
  const isFetch = job.type === JobType.Fetch
  // A FETCH is one opaque SSH round trip, so it has no honest percentage.
  const indeterminate = isFetch || job.total <= 0
  const failedNote = job.failed > 0 ? ` · ${formatNumber(job.failed)} failed` : ''

  let headline = active ? RUNNING_COPY[job.type] : FINISHED_COPY[job.type]
  let tone: ProgressTone = job.failed > 0 ? 'warning' : 'accent'
  let detail: string
  let glyph: ReactElement = <Spinner size="sm" />
  /** Second line, only while queued: why the wait exists and when it ends. */
  let hint: string | null = null

  if (waitingForSlot) {
    glyph = <Hourglass aria-hidden="true" />
    headline =
      position > 0
        ? `Waiting for a free generation slot — ${ordinal(position)} in line`
        : 'Waiting for a free generation slot'
    detail =
      job.total > 0
        ? `${formatNumber(job.total)} ${pluralize(job.total, 'image')} ready to generate — none started yet. You can leave this page; the batch keeps its place in line.`
        : 'Nothing has started yet.'
    hint =
      maxUsers === null
        ? 'This instance generates for a limited number of users at a time — your batch starts on its own the moment a slot frees.'
        : `This instance generates for ${formatNumber(maxUsers)} ${pluralize(maxUsers, 'user')} at a time — your batch starts on its own the moment a slot frees.`
  } else if (queued) {
    glyph = QUEUED_ICONS[job.type]
    headline = `${RUNNING_COPY[job.type]} — queued`
    detail = 'Nothing has started yet.'
    hint = 'Waiting for this site’s current job to finish, then it starts on its own.'
  } else if (active) {
    detail = isFetch
      ? (job.message ?? 'Reading the WordPress media library over SSH…')
      : `${formatNumber(processed)} of ${formatNumber(job.total)} processed${failedNote}`
  } else if (job.status === JobStatus.Completed) {
    tone = job.failed > 0 ? 'warning' : 'success'
    glyph = job.failed > 0 ? <TriangleAlert aria-hidden="true" /> : <CircleCheck aria-hidden="true" />
    detail = isFetch
      ? 'The media library is in sync with WordPress.'
      : `${formatNumber(job.completed)} ${pluralize(job.completed, 'image')} finished${failedNote}`
  } else if (job.status === JobStatus.Cancelled) {
    headline = `${RUNNING_COPY[job.type]} — cancelled`
    tone = 'warning'
    glyph = <CircleSlash aria-hidden="true" />
    detail = isFetch
      ? 'Cancelled before the library finished loading.'
      : `Stopped after ${formatNumber(processed)} of ${formatNumber(job.total)}${failedNote}`
  } else {
    headline = `${RUNNING_COPY[job.type]} — failed`
    tone = 'danger'
    glyph = <TriangleAlert aria-hidden="true" />
    detail = job.error ?? 'The job stopped before it finished.'
  }

  return (
    <section
      aria-label={`${RUNNING_COPY[job.type]} status`}
      className={cn(
        'flex items-start gap-3 rounded-card border bg-surface p-4 shadow-sheet',
        shells[tone]
      )}
    >
      <span
        className={cn(
          'grid size-9 shrink-0 place-items-center rounded-control [&_svg]:size-[1.125rem]',
          glyphs[tone]
        )}
      >
        {glyph}
      </span>

      <div className="min-w-0 flex-1" aria-live="polite" aria-atomic="true">
        <p className="text-sm leading-tight font-semibold text-fg">{headline}</p>
        {active && !queued ? (
          <ProgressBar
            className="mt-2"
            label={detail}
            value={jobProgressPct(job)}
            indeterminate={indeterminate}
            tone={tone}
          />
        ) : (
          <p className="mt-1 text-xs leading-relaxed text-fg-muted">{detail}</p>
        )}
        {hint ? <p className="mt-1.5 text-xs leading-relaxed text-fg-subtle">{hint}</p> : null}
        {active && !queued && !indeterminate && job.message ? (
          <p className="mt-2 truncate text-xs text-fg-subtle">{job.message}</p>
        ) : null}
      </div>

      {active ? (
        <Button
          size="sm"
          variant="secondary"
          iconLeft={<Ban />}
          onClick={onCancel}
          loading={cancelling}
        >
          {queued ? 'Leave the queue' : 'Cancel'}
        </Button>
      ) : null}
    </section>
  )
}
